/**
 * What can be done to a role, from its row and from its detail header alike.
 *
 * Two actions: **rename** and **delete**. A role's permissions are deliberately a
 * different edit — `PUT /roles/:id/permissions` takes the **complete** set so
 * two administrators cannot half-apply each other's changes, and folding a
 * rename into that endpoint would cost exactly that property. See
 * `../people/README.md`.
 *
 * That is also why the permission editor's Save and Discard are *not* here.
 * They act on a draft that exists only while `RoleDetailPage` is open; a table
 * row has no draft, so there is nothing to mirror. They stay the screen's own.
 *
 * The label says **"Rename this role"** and not "Edit": a role row carries both
 * ways in — the name links to the permission editor, the icon renames — and a
 * pencil labelled "Edit" beside that link reads as a shortcut to the page the
 * link already reaches.
 *
 * Deleting takes every assignment with it, so the confirmation loads who holds
 * the role first and says so. A built-in role offers neither act, disabled with
 * the reason — the seed would undo both.
 */
import { useTranslation } from 'react-i18next';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { RoleDto, RoleUsage } from '@forsys/shared';
import { deleteRole, getRoleUsage, updateRole } from '../../api/identity';
import { useSelfLockout, withLockoutDialog } from '../../auth/useSelfLockout';
import { BLANK_NAMED_FORM, namedChanges, namedFormFor, type NamedForm } from '../../utils/namedForm';
import {
  confirmAction,
  formAction,
  useEntityActions,
  type EntityActionsState,
} from '../../hooks/useEntityActions';

/** A role's name and description — the same form a group has. */
export type RoleForm = NamedForm;

export const BLANK_ROLE_FORM: RoleForm = BLANK_NAMED_FORM;

/**
 * The fields, exported so `RolesPage`'s create dialog renders the identical
 * ones from its own state. Creating is the screen's act, not the entity's.
 */
export function useRoleFields() {
  const { t } = useTranslation('people');

  return (form: RoleForm, patch: (changes: Partial<RoleForm>) => void) => (
    <>
      <TextField
        label={t('roles.createDialog.name')}
        value={form.name}
        onChange={(event) => patch({ name: event.target.value })}
        autoFocus
        fullWidth
      />
      <TextField
        label={t('roles.createDialog.description')}
        value={form.description}
        onChange={(event) => patch({ description: event.target.value })}
        multiline
        minRows={2}
        fullWidth
      />
    </>
  );
}

/**
 * The note under the fields, which differs between creating and renaming.
 *
 * Outside the fields, because the create dialog says something else — a new
 * role starts with no permissions and is filled in on its detail screen — and
 * one function with a mode flag is how the two sentences end up describing each
 * other's case.
 */
export function RoleFormNote({ mode }: { mode: 'create' | 'edit' }) {
  const { t } = useTranslation('people');
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {mode === 'edit' ? t('roles.editDialog.note') : t('roles.createDialog.note')}
      </Typography>
    </Box>
  );
}

/** Who loses the role, in the delete confirmation. */
function RoleDeleteMessage({ usage }: { usage: RoleUsage }) {
  const { t } = useTranslation('people');
  const held = usage.userAssignments + usage.groupAssignments > 0;

  return (
    <>
      {held ? (
        <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 3 }}>
          {usage.userAssignments > 0 && (
            <li>{t('roles.deleteDialog.users', { count: usage.userAssignments })}</li>
          )}
          {usage.groupAssignments > 0 && (
            <li>{t('roles.deleteDialog.groups', { count: usage.groupAssignments })}</li>
          )}
        </Box>
      ) : (
        <DialogContentText sx={{ mb: 1.5 }}>{t('roles.deleteDialog.nobody')}</DialogContentText>
      )}
      <DialogContentText>{t('roles.deleteDialog.consequence')}</DialogContentText>
    </>
  );
}

export interface UseRoleActionsOptions {
  /** After a successful rename. Reload the list, or hand the record over. */
  onChanged: (role: RoleDto) => void;
  /** After a delete. Reload the list, or leave the detail screen. */
  onDeleted: (role: RoleDto) => void;
}

export function useRoleActions(options: UseRoleActionsOptions): EntityActionsState<RoleDto> {
  const { t } = useTranslation('people');
  const fields = useRoleFields();
  const lockout = useSelfLockout();

  const state = useEntityActions<RoleDto>(
    [
      formAction<RoleDto, RoleForm>({
        kind: 'form',
        key: 'rename',
        label: t('roles.edit'),
        icon: <EditOutlinedIcon fontSize="small" />,
        permission: 'role.update',

        // A built-in role is shown the action, disabled, with the reason as its
        // label. The reason is stronger than consistency with the permission
        // editor: the seed finds `Administrator` by its normalised name
        // (`db/seeds/001_builtin_roles.ts`), so a renamed one is a role the next
        // deployment no longer recognises — it would create a *second*
        // Administrator holding every permission while the renamed one kept its
        // own set.
        lockedReason: (role) => (role.isBuiltin ? t('roles.editBuiltin') : null),

        blank: BLANK_ROLE_FORM,
        formFor: namedFormFor,
        fields,
        footer: <RoleFormNote mode="edit" />,
        incomplete: (form) => form.name.trim() === '',
        title: (role) => t('roles.editDialog.title', { name: role.name }),
        submitLabel: t('roles.editDialog.submit'),

        run: async (role, form) => {
          const changes = namedChanges(role, form);
          // An empty PATCH is refused by the server rather than writing an audit
          // row for a change that did not happen, so nothing is sent.
          if (Object.keys(changes).length === 0) return { role };
          return updateRole(role.id, changes);
        },
      }),

      confirmAction<RoleDto, RoleUsage>({
        kind: 'confirm',
        key: 'delete',
        label: t('roles.delete'),
        icon: <DeleteOutlinedIcon fontSize="small" />,
        permission: 'role.delete',
        tone: 'error',
        // The server refuses with 403 `builtin_role`: the seed would create it
        // again on the next deployment, holding every permission.
        lockedReason: (role) => (role.isBuiltin ? t('roles.deleteBuiltin') : null),
        title: (role) => t('roles.deleteDialog.title', { name: role.name }),
        load: async (role) => (await getRoleUsage(role.id)).usage,
        message: (_role, usage) => <RoleDeleteMessage usage={usage} />,
        confirmLabel: t('roles.deleteDialog.confirm'),
        // Resolves to the role itself, so `onDeleted` knows which one went.
        run: async (role) => {
          // Deleting a role you hold can take your own permissions with it.
          await lockout.guard((accept) => deleteRole(role.id, accept));
          return { deleted: role };
        },
      }),
    ],
    {
      onChanged: (result, key) => {
        if (key === 'delete') options.onDeleted((result as { deleted: RoleDto }).deleted);
        else options.onChanged((result as { role: RoleDto }).role);
      },
    },
  );

  return withLockoutDialog(state, lockout.dialog);
}
